/**
 * 콘텐츠 한 건의 작은 썸네일 — 플레이리스트 편성 줄·담기 격자(`Content.tsx`)와 바로 보내기 창(`DirectSendModal.tsx`)이
 * 같은 그림을 쓴다. 종류마다 보여 줄 수 있는 것이 다르다 — 없는 것을 있는 척하지 않는다(`MediaThumbs.tsx`).
 */
import type { ReactNode } from 'react';
import { Image as ImageIcon } from 'lucide-react';
import type { Asset } from '../api/client.js';
import { TemplateThumb, VideoThumb, WebThumb } from './MediaThumbs.js';
import { HtmlThumb } from './HtmlContent.js';

export function AssetThumbSmall({
  asset,
  full = false,
  className,
}: {
  asset: Asset | undefined;
  /** 내용까지 그린다(웹 페이지·HTML 을 줄여 띄우고 영상 길이를 단다). 주지 않으면 편성 줄의 64px 칸 — 종류만. */
  full?: boolean;
  /** 칸의 크기·테두리. 주지 않으면 `full` 은 격자 칸을 꽉 채우고, 아니면 편성 줄의 64×36. */
  className?: string;
}): ReactNode {
  const base =
    className ??
    (full
      ? 'aspect-video w-full overflow-hidden bg-slate-100'
      : 'h-9 w-16 shrink-0 overflow-hidden rounded border border-slate-200 bg-slate-100');

  // 지워졌거나 목록에 없는 자산. 빈 네모라도 두어야 줄이 어긋나지 않는다.
  if (!asset) {
    return (
      <div className={`${base} flex items-center justify-center`} aria-hidden="true">
        <span className="text-[10px] text-slate-400">?</span>
      </div>
    );
  }

  if (asset.type === 'template' && asset.template) {
    // 화면 가까이 온 칸만 그린다 — 담기 격자에 템플릿이 수백 개면 iframe 이 한꺼번에 선다.
    return <TemplateThumb template={asset.template} location="" className={`${base} bg-slate-900`} />;
  }

  if (asset.type === 'image' && asset.preview_url) {
    return (
      <div className={base}>
        <img src={asset.preview_url} alt="" className="h-full w-full object-cover" loading="lazy" />
      </div>
    );
  }

  // 영상·웹은 그 자리에서 만든 장면·페이지 — 아이콘만으로는 영상 열 개가 모두 같아 보였다.
  // 편성 줄(64px)의 웹은 사이트 아이콘만, 고르기 격자는 페이지를 스크립트 없이 줄여 그린다.
  if (asset.type === 'video') return <VideoThumb asset={asset} className={base} compact={!full} />;
  if (asset.type === 'web') return <WebThumb asset={asset} className={base} live={full} />;
  // 올린 HTML 은 고르기 격자에서 출처 없는 샌드박스로 줄여 띄우고, 편성 줄에서는 종류만(`HtmlContent.tsx`).
  if (asset.type === 'html') return <HtmlThumb asset={asset} className={base} compact={!full} />;

  return (
    <div className={`${base} flex items-center justify-center`}>
      <ImageIcon className="h-4 w-4 text-slate-300" strokeWidth={1.8} />
    </div>
  );
}
